import { useTranslation } from "react-i18next";
import { overlayClose } from "../../../shared/utils/overlayClose";

/**
 * AccountingDetailModal — modal de "detalle" con panel Información General +
 * Detalle Contable (DEBE/HABER, resumen y check de balance). Diseño compartido
 * entre el detalle de Factura CxP y el detalle de Recibo CxP (cualquier
 * documento que genere un asiento simple de 2 líneas).
 */
export default function AccountingDetailModal({
  onClose, onEdit,
  icon = "fa-file-invoice", iconColor = "#f97316", iconBg = "rgba(249,115,22,0.15)",
  title, badge, subtitle,
  infoTitle, infoIcon = "fa-circle-info", infoRows = [],
  contableTitle, contableIcon = "fa-scale-balanced",
  debeLabel, debeCuenta, debeColor = "#f87171", debeBg = "rgba(248,113,113,0.15)",
  haberLabel, haberCuenta, haberColor = "#60a5fa", haberBg = "rgba(96,165,250,0.15)",
  montoFmt,
}) {
  const { t } = useTranslation();
  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 1000,
      background: "rgba(0,0,0,0.72)", backdropFilter: "blur(3px)",
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }} {...overlayClose(onClose)}>
      <div style={{
        background: "var(--bg-card)", border: "1px solid var(--border-card)",
        borderRadius: 14, width: "min(720px, 96vw)", maxHeight: "92vh", overflow: "auto",
        boxShadow: "0 24px 64px rgba(0,0,0,0.55)",
      }} onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "flex-start",
          padding: "18px 22px", borderBottom: "1px solid var(--border-card)", gap: 10,
        }}>
          <div style={{ display: "flex", gap: 12, alignItems: "flex-start", minWidth: 0 }}>
            <div style={{ width: 42, height: 42, borderRadius: 10, flexShrink: 0,
              background: iconBg, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <i className={`fa-solid ${icon}`} style={{ color: iconColor, fontSize: "1.05rem" }} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 700, color: "var(--text-primary)",
                  overflow: "hidden", textOverflow: "ellipsis" }}>
                  {title}
                </h2>
                {badge && (
                  <span style={{
                    fontSize: "0.68rem", fontWeight: 700, padding: "2px 9px", borderRadius: 20,
                    background: "rgba(249,115,22,0.15)", border: "1px solid rgba(249,115,22,0.4)",
                    color: "#f97316", whiteSpace: "nowrap",
                  }}>{badge}</span>
                )}
              </div>
              {subtitle && (
                <p style={{ margin: "3px 0 0", color: "var(--text-muted)", fontSize: "0.8rem" }}>{subtitle}</p>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none",
            color: "var(--text-muted)", cursor: "pointer", fontSize: "1.1rem", padding: 4, flexShrink: 0 }}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: 22, display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 18 }}>

          {/* Información General */}
          <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
              borderBottom: "1px solid var(--border-card)", background: "var(--hover-bg)" }}>
              <i className={`fa-solid ${infoIcon}`} style={{ color: "#f97316", fontSize: "0.78rem" }} />
              <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-primary)" }}>
                {infoTitle}
              </span>
            </div>
            <div style={{ padding: "4px 14px" }}>
              {infoRows.map(([label, value, highlight], i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  padding: "10px 0", gap: 10,
                  borderBottom: i < infoRows.length - 1 ? "1px solid var(--border-inner)" : "none",
                }}>
                  <span style={{ color: "var(--text-secondary)", fontSize: "0.8rem" }}>{label}</span>
                  <span style={{
                    color: highlight ? "#f97316" : "var(--text-primary)",
                    fontWeight: highlight ? 800 : 600,
                    fontSize: highlight ? "0.98rem" : "0.85rem",
                    fontFamily: highlight ? "monospace" : undefined,
                    textAlign: "right", maxWidth: "60%", wordBreak: "break-word",
                  }}>{value}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Detalle Contable */}
          <div style={{ border: "1px solid var(--border-card)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px",
              borderBottom: "1px solid var(--border-card)", background: "var(--hover-bg)" }}>
              <i className={`fa-solid ${contableIcon}`} style={{ color: "#f97316", fontSize: "0.78rem" }} />
              <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--text-primary)" }}>
                {contableTitle}
              </span>
            </div>
            <div style={{ padding: 14 }}>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", marginBottom: 5 }}>{debeLabel}</div>
                <span style={{
                  display: "inline-block", padding: "5px 12px", borderRadius: 7, maxWidth: "100%",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  background: debeBg, color: debeColor, fontSize: "0.82rem", fontWeight: 700,
                }}>{debeCuenta}</span>
              </div>
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: "0.72rem", color: "var(--text-secondary)", marginBottom: 5 }}>{haberLabel}</div>
                <span style={{
                  display: "inline-block", padding: "5px 12px", borderRadius: 7, maxWidth: "100%",
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  background: haberBg, color: haberColor, fontSize: "0.82rem", fontWeight: 700,
                }}>{haberCuenta}</span>
              </div>

              {/* Resumen Contable */}
              <div style={{ background: "var(--bg)", border: "1px solid var(--border-card)",
                borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "var(--text-secondary)",
                  marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.4px" }}>
                  {t("cxp.resumenContable")}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8, marginBottom: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, color: debeColor }}>{debeLabel}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{debeCuenta}</div>
                  </div>
                  <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "0.9rem", color: debeColor, flexShrink: 0 }}>
                    {montoFmt}
                  </span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: "0.72rem", fontWeight: 700, color: haberColor }}>{haberLabel}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--text-secondary)",
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{haberCuenta}</div>
                  </div>
                  <span style={{ fontFamily: "monospace", fontWeight: 800, fontSize: "0.9rem", color: haberColor, flexShrink: 0 }}>
                    {montoFmt}
                  </span>
                </div>
              </div>

              {/* Balanceado */}
              <div style={{ display: "flex", gap: 10, alignItems: "flex-start",
                background: "rgba(74,222,128,0.08)", border: "1px solid rgba(74,222,128,0.3)",
                borderRadius: 8, padding: "10px 12px" }}>
                <i className="fa-solid fa-circle-check" style={{ color: "#4ade80", fontSize: "0.95rem", marginTop: 1 }} />
                <div>
                  <div style={{ color: "#4ade80", fontWeight: 700, fontSize: "0.8rem" }}>
                    {t("cxp.asientoBalanceado")}
                  </div>
                  <div style={{ color: "var(--text-secondary)", fontSize: "0.74rem", marginTop: 1 }}>
                    {t("cxp.debeHaberCoinciden")}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10,
          padding: "14px 22px", borderTop: "1px solid var(--border-card)" }}>
          {onEdit && (
            <button onClick={onEdit} style={{
              padding: "8px 18px", background: "transparent", border: "1px solid var(--border-card)",
              borderRadius: 7, color: "var(--text-primary)", fontSize: "0.83rem", fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 7,
            }}>
              <i className="fa-solid fa-pen" />
              {t("common.editar")}
            </button>
          )}
          <button onClick={onClose} style={{
            padding: "8px 18px", background: "#f97316", border: "none",
            borderRadius: 7, color: "#fff", fontSize: "0.83rem", fontWeight: 700, cursor: "pointer",
            display: "flex", alignItems: "center", gap: 7,
          }}>
            <i className="fa-solid fa-xmark" />
            {t("common.cerrar")}
          </button>
        </div>
      </div>
    </div>
  );
}
